import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, FileCheck2, Loader2, X, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp';
const MAX_MB = 10;

/**
 * KYC document uploader.
 * Sends multipart to POST /api/uploads/kyc?document_type=kbis|id_document
 * On success, returns the file metadata via the `onUploaded` callback
 * (and the backend automatically updates the vendor profile's URL field).
 */
export const KycUpload = ({ documentType = 'kbis', currentUrl = null, onUploaded, label, hint }) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(currentUrl);
  const inputRef = useRef(null);

  const T = (fr, en) => (language === 'fr' ? fr : en);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(T(`Fichier trop volumineux (max ${MAX_MB} Mo)`, `File too large (max ${MAX_MB} MB)`));
      e.target.value = '';
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(
        `${API}/uploads/kyc?document_type=${documentType}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setUploaded(res.data.url);
      toast.success(T('Document envoyé', 'Document uploaded'));
      if (onUploaded) onUploaded(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur d\'envoi', 'Upload error'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {label && <p className="text-sm font-medium text-[#0F4C5C]">{label}</p>}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div
        className={`border-2 border-dashed rounded-xl p-5 transition-colors ${
          uploaded ? 'border-[#2A9D8F]/40 bg-[#2A9D8F]/5' : 'border-[#0F4C5C]/20 hover:border-[#0F4C5C]/40'
        }`}
      >
        {uploaded ? (
          <div className="flex items-center gap-3">
            <FileCheck2 className="w-6 h-6 text-[#2A9D8F] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0F4C5C]">{T('Document envoyé', 'Document uploaded')}</p>
              <p className="text-xs text-muted-foreground truncate">{uploaded}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setUploaded(null); inputRef.current?.click(); }}
              data-testid={`kyc-upload-replace-${documentType}`}
            >
              <X className="w-4 h-4 mr-1" />
              {T('Remplacer', 'Replace')}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#0F4C5C]/40 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0F4C5C]">
                {T('Ajouter un document (PDF, JPG, PNG)', 'Add a document (PDF, JPG, PNG)')}
              </p>
              <p className="text-xs text-muted-foreground">{T(`Max ${MAX_MB} Mo`, `Max ${MAX_MB} MB`)}</p>
            </div>
            <Button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="btn-primary"
              data-testid={`kyc-upload-${documentType}`}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {T('Téléverser', 'Upload')}
            </Button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};
